import React from 'react';
import { RocketLaunchIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { Link } from 'react-router-dom';

const FeedbackCultureStage3 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 3: Embedding Empathy"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading
      >
        {/* TL;DR Section */}
        <div className="prose prose-invert max-w-none mb-6">
          <h3 className="text-lg font-semibold text-primary-light">TL;DR</h3>
          <ul className="list-disc pl-5 text-gray-300">
            <li>Celebrate and ritualize generous feedback-giving.</li>
            <li>Run a 4-week retro to reflect and iterate on your feedback culture.</li>
          </ul>
        </div>
        <ExpandableCallout
          type="info"
          title="What do I need to DO in this stage?"
          defaultOpen={true}
        >
          <div className="space-y-4">
            <p className="text-gray-300">You need to do 2 things:</p>
            <ol className="space-y-2 list-decimal list-inside">
              <li className="text-gray-300">Celebrate folks who are generous givers</li>
              <li className="text-gray-300">Run a retro after 4 weeks to unpack what's working/isn't/learns</li>
            </ol>
            <p className="text-gray-300 italic">Here's what we mean...</p>
          </div>
        </ExpandableCallout>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Celebrate folks who are generous givers</h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              In <Link to="/playbooks/feedback-culture/stage-2" className="text-primary hover:text-primary-light underline">Stage 2</Link> if you created a slack (or similar) channel to praise folks publicly for giving great feedback 
              then you might already be winning here.
            </p>
          </div>
          <div className="space-y-4">
            <p className="text-gray-300">It might take:</p>
            <ol className="space-y-2 list-decimal list-inside">
              <li className="text-gray-300">
                You leading out here (proactively seeking lots of feedback and saying thank you in #feedback-thanks to get things rolling)
              </li>
              <li className="text-gray-300">Some gentle nudges</li>
              <li className="text-gray-300">A bit of leadership role-modelling (inviting the leadership team to ask each other/DRs)</li>
            </ol>
            <ExpandableCallout
              type="tip"
              title="Pro Tip"
            >
              <p className="text-gray-300">
                One thing is to introduce a bit of a ritual here and incorporate a fun award into your monthly/quarterly all hands meeting 
                where you publicly celebrate the person who has been the most generous giver. Suno.ai + tome.app can create a fun little 
                30second celebration video ;)
              </p>
            </ExpandableCallout>
            <p className="text-gray-300 mt-4">
              The main thing you are going for is trying to convert:
            </p>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
              <p className="text-gray-300 font-semibold">
                Early momentum {'->'} Ritual
              </p>
            </div>
          </div>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Run a Retrospective</h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Whether you've managed to achieve this or not, it's a good idea to run a +4 week checkpoint in the form of a retro 
              (synch/async depending on your ways of working). Think about introducing feedback like a 4 week experiment you've 
              just run on your culture and borrow from the Agile Coach toolkit a bit here to dig into:
            </p>
          </div>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              Barriers to getting into it
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              Things that have worked well / haven't
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              Surprising benefits
            </li>
          </ul>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 mt-4">
              There's a gazillion different ways to run a retro. Personally I like the simplicity of the 4 Ls:
            </p>
          </div>
          <div className="mt-4">
            <ToolCallout
              tool="miro"
              label="4 L's Retrospective Miro Template"
              href="https://miro.com/templates/4-ls-retrospective/"
              description="A ready-to-use Miro board for running a 4 L's retrospective."
            />
          </div>
          <h3 className="text-lg font-semibold text-white mt-6">Action:</h3>
          <ul className="space-y-2">
            <li>
              <div className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  Facilitate retrospective
                  <ul className="mt-2 space-y-1 ml-6 list-disc text-gray-300">
                    <li>Invite folks to contribute stickys/thoughts and comments to the board</li>
                    <li>Unpack themes with comments/1-2-1 chats</li>
                    <li>Summarise findings + propose actions</li>
                    <li>Publish for comment + commitment</li>
                  </ul>
                </div>
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Iterate on the new version of your feedback culture incorporating the insights gleaned from the retro</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>REPEAT</span>
            </li>
          </ul>
          <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
            <p className="text-gray-300 font-semibold">By the end of this stage you should have:</p>
            <ul className="mt-2 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                A gentle flow of '🙏 Thank yous' in #feedback-thanks and some kind of celebration of feedback ritualised. It's now 'a thing we do'
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                A bunch of learns from the retro that you can action/support others with
              </li>
            </ul>
          </div>
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
};

export default FeedbackCultureStage3; 